import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, collectionGroup } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// This component is the user's dashboard for viewing their past requests.
export default function MyRequests({ userProfile, onSelectItem, appId }) {
    const [myItems, setMyItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

    // --- STEP 1: Add state for pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }
    
        setIsLoading(true);
    
        const requestsQuery = query(collection(db, `/artifacts/${appId}/public/data/purchaseRequests`), where("requesterId", "==", userProfile.uid));
        const reqUnsubscribe = onSnapshot(requestsQuery, (reqSnapshot) => {
            const requestsData = reqSnapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Purchase' }));
            setMyItems(current => {
                const otherItems = current.filter(item => item.type !== 'Purchase');
                return [...requestsData, ...otherItems];
            });
            setIsLoading(false);
        });
    
        const invoicesQuery = query(collectionGroup(db, 'invoices'), where("submittedById", "==", userProfile.uid));
        const invUnsubscribe = onSnapshot(invoicesQuery, (invSnapshot) => {
            const invoicesData = invSnapshot.docs.map(d => ({ ...d.data(), id: d.id, type: 'Invoice' }));
            setMyItems(current => {
                const otherItems = current.filter(item => item.type !== 'Invoice');
                return [...invoicesData, ...otherItems];
            });
            setIsLoading(false);
        });
    
        return () => {
            reqUnsubscribe();
            invUnsubscribe();
        };
    }, [userProfile, appId]);

    const processedItems = useMemo(() => {
        let filtered = [...myItems];

        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(item => {
                const vendor = item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor) || '';
                const description = item.description || `Invoice for PR: ${item.requestNumber}` || '';
                const id = item.requestNumber || item.invoiceNumber || '';

                return (
                    vendor.toLowerCase().includes(lowercasedTerm) ||
                    description.toLowerCase().includes(lowercasedTerm) ||
                    id.toLowerCase().includes(lowercasedTerm)
                );
            });
        }

        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'createdAt' && a.createdAt?.toDate) {
                aValue = a.createdAt.toDate();
                bValue = b.createdAt.toDate();
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        return filtered;
    }, [myItems, searchTerm, sortConfig]);
    
    // --- STEP 2: Calculate the data for the current page ---
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return processedItems.slice(startIndex, endIndex);
    }, [processedItems, currentPage, itemsPerPage]);

    // --- STEP 3: Create handlers and variables for pagination controls ---
    const totalPages = Math.ceil(processedItems.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1); // Reset to first page on sort
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <h2 className="text-2xl font-semibold text-[#213368] mb-4">My Requests & Invoices</h2>
            
            <div className="mb-4">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by ID, vendor, or description..."
                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                />
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('type')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Type</th>
                                <th onClick={() => requestSort('requestNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">ID</th>
                                <th onClick={() => requestSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Date Requested</th>
                                <th onClick={() => requestSort('vendorName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Vendor</th>
                                <th onClick={() => requestSort('description')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Description</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {/* --- STEP 4: Render the paginated data --- */}
                            {paginatedItems.length > 0 ? (
                                paginatedItems.map(item => (
                                    <ItemRow 
                                        key={item.id}
                                        item={item}
                                        onSelectItem={onSelectItem}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-12 px-6">
                                        <h3 className="text-sm font-medium text-[#213368]">No Requests Found</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {searchTerm ? 'Your search returned no results.' : 'You have not created any purchase requests or submitted any invoices yet.'}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* --- STEP 5: Add pagination controls --- */}
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ItemRow({ item, onSelectItem }) {

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Pending Manager Approval':
            case 'Pending Invoice Approval':
                 return 'bg-yellow-100 text-yellow-800';
            case 'Purchase Approved; Vendor Pending Onboarding':
                return 'bg-cyan-100 text-cyan-800';
            case 'Approved':
            case 'Approved for Payment':
                 return 'bg-green-100 text-green-800';
            case 'Rejected':
            case 'Rejected by Finance':
                 return 'bg-red-100 text-red-800';
            case 'Expired': return 'bg-gray-200 text-gray-600';
            case 'Paid': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    
    const isClickable = item.type === 'Purchase';
    const rejectionReason = item.status === 'Rejected' ? item.approvalComments : 
                            item.status === 'Rejected by Finance' ? item.financeRejectionReason : '';

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <tr 
            className={`${isClickable ? 'cursor-pointer hover:bg-gray-50' : ''}`} 
            onClick={() => isClickable && onSelectItem(item)}
        >
            <td className="px-6 py-4 whitespace-nowrap text-sm">
               <span className={`px-2 py-1 text-xs rounded-full ${item.type === 'Purchase' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{item.type}</span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-[#D95448]">
                {item.type === 'Purchase' ? item.requestNumber : item.invoiceNumber}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(item.createdAt)}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 truncate max-w-xs">{item.vendorName || (item.vendorType === 'new' ? item.newVendorName : item.selectedVendor)}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#213368] truncate max-w-xs">{item.description || `Invoice for PR: ${item.requestNumber}`}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div title={rejectionReason}>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(item.status)}`}>
                        {item.status}
                    </span>
                </div>
            </td>
        </tr>
    );
}